<template>
  <div class="dashboard">
    <div class="header">
      <h1>🏠 洗衣房巡检管理系统</h1>
      <p>实时监控洗衣房状态，提升管理效率</p>
      
      <!-- 管理员学校选择器 -->
      <div v-if="userRole === 'admin'" class="school-selector">
        <el-select 
          v-model="selectedSchoolId" 
          placeholder="选择学校查看数据" 
          clearable 
          style="width: 250px;"
          @change="onSchoolChange"
        >
          <el-option label="查看所有学校" :value="''"/>
          <el-option 
            v-for="school in schoolList" 
            :key="school.id" 
            :label="school.name" 
            :value="school.id"
          />
        </el-select>
        <span class="current-school" v-if="selectedSchoolId">
          当前查看：{{ getSelectedSchoolName() }}
        </span>
      </div>
    </div>

    <!-- 统计卡片 -->
    <div class="stats-grid">
      <div class="stat-card total">
        <div class="stat-header">
          <div>
            <div class="stat-number">{{ statistics.totalLaundry }}</div>
            <div class="stat-label">洗衣房总数</div>
            <div class="stat-trend trend-up">较上月 +{{ laundryGrowth }}</div>
          </div>
          <div class="stat-icon">🏠</div>
        </div>
      </div>

      <div class="stat-card normal">
        <div class="stat-header">
          <div>
            <div class="stat-number">{{ statistics.normalLaundry }}</div>
            <div class="stat-label">正常运行</div>
            <div class="stat-trend trend-up">运行率 {{ normalRate }}%</div>
          </div>
          <div class="stat-icon">✅</div>
        </div>
      </div>

      <div class="stat-card warning">
        <div class="stat-header">
          <div>
            <div class="stat-number">{{ statistics.abnormalLaundry }}</div>
            <div class="stat-label">异常状态</div>
            <div class="stat-trend trend-down">需关注</div>
          </div>
          <div class="stat-icon">⚠️</div>
        </div>
      </div>

      <div class="stat-card danger">
        <div class="stat-header">
          <div>
            <div class="stat-number">{{ statistics.repairLaundry }}</div>
            <div class="stat-label">需要维修</div>
            <div class="stat-trend trend-down">待处理</div>
          </div>
          <div class="stat-icon">🔧</div>
        </div>
      </div>

      <div class="stat-card info">
        <div class="stat-header">
          <div>
            <div class="stat-number">{{ statistics.totalCleaner }}</div>
            <div class="stat-label">保洁员总数</div>
            <div class="stat-trend trend-up">在线 {{ onlineCleaners }} 人</div>
          </div>
          <div class="stat-icon">👥</div>
        </div>
      </div>

      <div class="stat-card purple">
        <div class="stat-header">
          <div>
            <div class="stat-number">{{ statistics.todayInspection }}</div>
            <div class="stat-label">今日巡检</div>
            <div class="stat-trend trend-up">目标达成 {{ inspectionRate }}%</div>
          </div>
          <div class="stat-icon">📋</div>
        </div>
      </div>
    </div>

    <!-- 图表区域 -->
    <div class="charts-section">
      <div class="chart-card">
        <div class="chart-title">
          📊 巡检状态分布
        </div>
        <div class="chart-container" ref="statusChart" v-loading="chartLoading"></div>
      </div>

      <div class="chart-card">
        <div class="chart-title">
          📈 近7天巡检趋势
        </div>
        <div class="chart-container" ref="trendChart" v-loading="chartLoading"></div>
      </div>
    </div>

    <!-- 最近活动 -->
    <div class="recent-activities full-width">
      <div class="chart-title">
        🕐 最近巡检活动
        <el-button style="float: right; padding: 3px 0" type="text" @click="viewAllInspection">
          查看全部
        </el-button>
      </div>
      <div v-loading="activitiesLoading">
        <div v-if="recentInspections.length === 0" class="empty-state">
          暂无最近巡检记录
        </div>
        <div v-else>
          <div
              v-for="item in recentInspections"
              :key="item.id"
              class="activity-item"
          >
            <div class="activity-icon" :class="getStatusClass(item.status)">
              {{ getStatusIcon(item.status) }}
            </div>
            <div class="activity-content">
              <div class="activity-title">{{ item.laundry_name }}</div>
              <div class="activity-desc">{{ item.cleaner_name }} 进行了巡检</div>
            </div>
            <div style="text-align: right;">
              <div class="status-badge" :class="`status-${getStatusType(item.status)}`">
                {{ item.status }}
              </div>
              <div class="activity-time">{{ getTimeAgo(item.created_at) }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 刷新按钮 -->
    <el-button
        class="refresh-btn"
        type="primary"
        icon="el-icon-refresh"
        circle
        @click="refreshData"
        :loading="refreshing"
        title="刷新数据"
    ></el-button>
  </div>
</template>

<script>
import * as echarts from 'echarts'

export default {
  name: 'Dashboard',
  data() {
    return {
      userRole: window.sessionStorage.getItem('userRole'),
      selectedSchoolId: '', // 管理员选择的学校ID
      schoolList: [], // 学校列表
      statistics: {
        totalLaundry: 0,
        normalLaundry: 0,
        abnormalLaundry: 0,
        repairLaundry: 0,
        totalCleaner: 0,
        todayInspection: 0,
        totalInspection: 0,
        pendingRepair: 0
      },
      recentInspections: [],
      weeklyTrend: [],
      chartLoading: false,
      activitiesLoading: false,
      refreshing: false,
      statusChart: null,
      trendChart: null,
      autoRefreshTimer: null
    }
  },
  computed: {
    normalRate() {
      if (this.statistics.totalLaundry === 0) return 0
      return ((this.statistics.normalLaundry / this.statistics.totalLaundry) * 100).toFixed(1)
    },
    inspectionRate() {
      const dailyGoal = 20 // 每日目标巡检次数
      return ((this.statistics.todayInspection / dailyGoal) * 100).toFixed(1)
    },
    onlineCleaners() {
      return Math.floor(this.statistics.totalCleaner * 0.8) // 假设80%在线
    },
    laundryGrowth() {
      return Math.floor(Math.random() * 3) // 模拟增长数据
    }
  },
  mounted() {
    this.initCharts()
    this.loadAllData()
    this.startAutoRefresh()

    // 监听窗口大小变化
    window.addEventListener('resize', this.handleResize)
  },
  beforeDestroy() {
    this.stopAutoRefresh()
    window.removeEventListener('resize', this.handleResize)

    // 销毁图表实例
    if (this.statusChart) {
      this.statusChart.dispose()
    }
    if (this.trendChart) {
      this.trendChart.dispose()
    }
  },
  methods: {
    // 初始化图表
    initCharts() {
      this.$nextTick(() => {
        this.statusChart = echarts.init(this.$refs.statusChart)
        this.trendChart = echarts.init(this.$refs.trendChart)
      })
    },

    // 加载所有数据
    async loadAllData() {
      try {
        // 如果是管理员，先加载学校列表
        if (this.userRole === 'admin') {
          await this.loadSchoolList()
        }
        
        await Promise.all([
          this.loadStatistics(),
          this.loadRecentInspections(),
          this.loadTrendData()
        ])
        this.updateCharts()
      } catch (error) {
        console.error('加载数据失败:', error)
        this.$message.error('加载数据失败')
      }
    },

    // 加载学校列表
    async loadSchoolList() {
      try {
        const { data: res } = await this.$http.get('/api/school/list')
        if (res.status === 0) {
          this.schoolList = res.data
        }
      } catch (error) {
        console.error('加载学校列表失败:', error)
        throw error
      }
    },

    // 加载统计数据
    async loadStatistics() {
      try {
        // 构建请求参数
        const params = {}
        if (this.userRole === 'admin' && this.selectedSchoolId) {
          params.school_id = this.selectedSchoolId
        }

        // 获取洗衣房统计
        const laundryRes = await this.$http.get('/api/laundry/list', { params })
        if (laundryRes.data.status === 0) {
          let laundryData = laundryRes.data.data
          // 如果是管理员且选择了特定学校，进行前端筛选
          if (this.userRole === 'admin' && this.selectedSchoolId) {
            laundryData = laundryData.filter(item => item.school_id == this.selectedSchoolId)
          }
          this.statistics.totalLaundry = laundryData.length
        }

        // 获取巡检统计
        const inspectionRes = await this.$http.get('/api/inspection/admin', { params })
        if (inspectionRes.data.status === 0) {
          const inspections = inspectionRes.data.data.list || []
          this.statistics.totalInspection = inspections.length

          // 统计各状态数量 - 基于最新状态
          const latestStatus = this.getLatestStatusByLaundry(inspections)
          const statusCount = {}
          Object.values(latestStatus).forEach(status => {
            statusCount[status] = (statusCount[status] || 0) + 1
          })

          this.statistics.normalLaundry = statusCount['正常'] || 0
          this.statistics.abnormalLaundry = statusCount['异常'] || 0
          this.statistics.repairLaundry = statusCount['需要维修'] || 0

          // 统计今日巡检
          const today = new Date().toISOString().split('T')[0]
          this.statistics.todayInspection = inspections.filter(item =>
              item.created_at.startsWith(today)
          ).length
        }

        // 获取保洁员统计
        const cleanerRes = await this.$http.get('/api/cleaner/list', { params })
        if (cleanerRes.data.status === 0) {
          let cleanerData = cleanerRes.data.data
          // 如果是管理员且选择了特定学校，进行前端筛选
          if (this.userRole === 'admin' && this.selectedSchoolId) {
            cleanerData = cleanerData.filter(item => item.school_id == this.selectedSchoolId)
          }
          this.statistics.totalCleaner = cleanerData.length
        }
      } catch (error) {
        console.error('加载统计数据失败:', error)
        throw error
      }
    },

    // 获取每个洗衣房的最新状态
    getLatestStatusByLaundry(inspections) {
      const latestStatus = {}
      inspections.forEach(item => {
        const laundryId = item.laundry_id
        if (!latestStatus[laundryId] ||
            new Date(item.created_at) > new Date(latestStatus[laundryId].created_at)) {
          latestStatus[laundryId] = item
        }
      })

      const statusMap = {}
      Object.keys(latestStatus).forEach(laundryId => {
        statusMap[laundryId] = latestStatus[laundryId].status
      })

      return statusMap
    },

    // 加载最近巡检记录
    async loadRecentInspections() {
      this.activitiesLoading = true
      try {
        const params = { page: 1, page_size: 8 }
        if (this.userRole === 'admin' && this.selectedSchoolId) {
          params.school_id = this.selectedSchoolId
        }
        
        const { data: res } = await this.$http.get('/api/inspection/admin', { params })
        if (res.status === 0) {
          this.recentInspections = res.data.list || []
        }
      } catch (error) {
        console.error('加载最近巡检记录失败:', error)
        throw error
      } finally {
        this.activitiesLoading = false
      }
    },

    // 加载趋势数据
    async loadTrendData() {
      try {
        // 这里可以调用专门的趋势API，暂时用现有数据模拟
        const params = {}
        if (this.userRole === 'admin' && this.selectedSchoolId) {
          params.school_id = this.selectedSchoolId
        }
        
        const { data: res } = await this.$http.get('/api/inspection/admin', { params })
        if (res.status === 0) {
          const inspections = res.data.list || []
          this.weeklyTrend = this.generateTrendData(inspections)
        }
      } catch (error) {
        console.error('加载趋势数据失败:', error)
        throw error
      }
    },

    // 生成趋势数据
    generateTrendData(inspections) {
      const last7Days = []
      const today = new Date()

      for (let i = 6; i >= 0; i--) {
        const date = new Date(today)
        date.setDate(date.getDate() - i)
        const dateStr = date.toISOString().split('T')[0]

        const dayInspections = inspections.filter(item =>
            item.created_at.startsWith(dateStr)
        )

        const statusCount = {
          normal: dayInspections.filter(item => item.status === '正常').length,
          abnormal: dayInspections.filter(item => item.status === '异常').length,
          repair: dayInspections.filter(item => item.status === '需要维修').length
        }

        last7Days.push({
          date: `${date.getMonth() + 1}-${date.getDate()}`,
          ...statusCount
        })
      }

      return last7Days
    },

    // 更新图表
    updateCharts() {
      this.chartLoading = true

      this.$nextTick(() => {
        try {
          this.updateStatusChart()
          this.updateTrendChart()
        } catch (error) {
          console.error('更新图表失败:', error)
        } finally {
          this.chartLoading = false
        }
      })
    },

    // 更新状态分布图
    updateStatusChart() {
      if (!this.statusChart) return

      const option = {
        tooltip: {
          trigger: 'item',
          formatter: '{a} <br/>{b}: {c} ({d}%)'
        },
        series: [{
          name: '巡检状态',
          type: 'pie',
          radius: ['40%', '70%'],
          center: ['50%', '50%'],
          data: [
            {
              value: this.statistics.normalLaundry,
              name: '正常',
              itemStyle: { color: '#48bb78' }
            },
            {
              value: this.statistics.abnormalLaundry,
              name: '异常',
              itemStyle: { color: '#ed8936' }
            },
            {
              value: this.statistics.repairLaundry,
              name: '需要维修',
              itemStyle: { color: '#f56565' }
            }
          ],
          emphasis: {
            itemStyle: {
              shadowBlur: 10,
              shadowOffsetX: 0,
              shadowColor: 'rgba(0, 0, 0, 0.5)'
            }
          },
          label: {
            fontSize: 12
          }
        }]
      }

      this.statusChart.setOption(option, true)
    },

    // 更新趋势图
    updateTrendChart() {
      if (!this.trendChart) return

      const option = {
        tooltip: {
          trigger: 'axis'
        },
        legend: {
          data: ['正常', '异常', '需要维修'],
          bottom: 0
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '15%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          data: this.weeklyTrend.map(item => item.date),
          axisLine: {
            lineStyle: {
              color: '#e2e8f0'
            }
          }
        },
        yAxis: {
          type: 'value',
          axisLine: {
            lineStyle: {
              color: '#e2e8f0'
            }
          }
        },
        series: [
          {
            name: '正常',
            type: 'line',
            data: this.weeklyTrend.map(item => item.normal),
            smooth: true,
            itemStyle: { color: '#48bb78' },
            areaStyle: {
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: 'rgba(72, 187, 120, 0.3)' },
                { offset: 1, color: 'rgba(72, 187, 120, 0.1)' }
              ])
            }
          },
          {
            name: '异常',
            type: 'line',
            data: this.weeklyTrend.map(item => item.abnormal),
            smooth: true,
            itemStyle: { color: '#ed8936' }
          },
          {
            name: '需要维修',
            type: 'line',
            data: this.weeklyTrend.map(item => item.repair),
            smooth: true,
            itemStyle: { color: '#f56565' }
          }
        ]
      }

      this.trendChart.setOption(option, true)
    },

    // 刷新数据
    async refreshData() {
      this.refreshing = true
      try {
        await this.loadAllData()
        this.$message.success('数据刷新成功')
      } catch (error) {
        this.$message.error('数据刷新失败')
      } finally {
        this.refreshing = false
      }
    },

    // 处理窗口大小变化
    handleResize() {
      if (this.statusChart) {
        this.statusChart.resize()
      }
      if (this.trendChart) {
        this.trendChart.resize()
      }
    },

    // 开始自动刷新
    startAutoRefresh() {
      this.autoRefreshTimer = setInterval(() => {
        this.loadAllData()
      }, 30000) // 30秒刷新一次
    },

    // 停止自动刷新
    stopAutoRefresh() {
      if (this.autoRefreshTimer) {
        clearInterval(this.autoRefreshTimer)
        this.autoRefreshTimer = null
      }
    },

    // 工具方法
    getTimeAgo(dateString) {
      if (!dateString) return '-'

      const now = new Date()
      const date = new Date(dateString)
      const diffMs = now - date
      const diffMins = Math.floor(diffMs / (1000 * 60))

      if (diffMins < 1) return '刚刚'
      if (diffMins < 60) return `${diffMins}分钟前`

      const diffHours = Math.floor(diffMins / 60)
      if (diffHours < 24) return `${diffHours}小时前`

      const diffDays = Math.floor(diffHours / 24)
      return `${diffDays}天前`
    },

    getStatusClass(status) {
      const classMap = {
        '正常': 'normal',
        '异常': 'warning',
        '需要维修': 'danger'
      }
      return classMap[status] || 'info'
    },

    getStatusIcon(status) {
      const iconMap = {
        '正常': '✅',
        '异常': '⚠️',
        '需要维修': '🔧'
      }
      return iconMap[status] || '📋'
    },

    getStatusType(status) {
      const typeMap = {
        '正常': 'normal',
        '异常': 'warning',
        '需要维修': 'danger'
      }
      return typeMap[status] || 'normal'
    },

    // 跳转到巡检管理页面
    viewAllInspection() {
      this.$router.push('/inspectionmanage')
      this.$nextTick(() => {
        window.sessionStorage.setItem('activePath', '/inspectionmanage')
      })
    },

    // 学校选择变化时的处理
    onSchoolChange() {
      this.loadAllData()
    },

    // 获取选中的学校名称
    getSelectedSchoolName() {
      if (!this.selectedSchoolId) return '所有学校'
      const school = this.schoolList.find(s => s.id == this.selectedSchoolId)
      return school ? school.name : '未知学校'
    }
  }
}
</script>

<style scoped>
.dashboard {
  padding: 20px;
  max-width: 1400px;
  margin: 0 auto;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  min-height: 100vh;
  color: #333;
}

.header {
  text-align: center;
  margin-bottom: 30px;
  color: white;
}

.header h1 {
  font-size: 2.5rem;
  margin-bottom: 10px;
  text-shadow: 2px 2px 4px rgba(0,0,0,0.3);
}

.header p {
  font-size: 1.1rem;
  opacity: 0.9;
}

.school-selector {
  margin-top: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 15px;
}

.current-school {
  color: #e2e8f0;
  font-size: 0.9rem;
  font-weight: 500;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
}

.stat-card {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border-radius: 16px;
  padding: 25px;
  box-shadow: 0 8px 32px rgba(0,0,0,0.1);
  border: 1px solid rgba(255,255,255,0.2);
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.stat-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: var(--accent-color);
}

.stat-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 12px 40px rgba(0,0,0,0.15);
}

.stat-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

.stat-icon {
  width: 50px;
  height: 50px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  color: white;
}

.stat-number {
  font-size: 2.5rem;
  font-weight: 700;
  color: #2d3748;
  margin-bottom: 5px;
}

.stat-label {
  font-size: 0.9rem;
  color: #718096;
  font-weight: 500;
}

.stat-trend {
  font-size: 0.8rem;
  margin-top: 8px;
}

.trend-up {
  color: #48bb78;
}

.trend-down {
  color: #f56565;
}

/* 颜色主题 */
.total { --accent-color: #667eea; }
.normal { --accent-color: #48bb78; }
.warning { --accent-color: #ed8936; }
.danger { --accent-color: #f56565; }
.info { --accent-color: #4299e1; }
.purple { --accent-color: #9f7aea; }

.total .stat-icon { background: #667eea; }
.normal .stat-icon { background: #48bb78; }
.warning .stat-icon { background: #ed8936; }
.danger .stat-icon { background: #f56565; }
.info .stat-icon { background: #4299e1; }
.purple .stat-icon { background: #9f7aea; }

.charts-section {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-bottom: 30px;
}

.chart-card {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border-radius: 16px;
  padding: 25px;
  box-shadow: 0 8px 32px rgba(0,0,0,0.1);
  border: 1px solid rgba(255,255,255,0.2);
}

.chart-title {
  font-size: 1.3rem;
  font-weight: 600;
  margin-bottom: 20px;
  color: #2d3748;
  display: flex;
  align-items: center;
  gap: 10px;
}

.chart-container {
  height: 300px;
}

.recent-activities {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border-radius: 16px;
  padding: 25px;
  box-shadow: 0 8px 32px rgba(0,0,0,0.1);
  border: 1px solid rgba(255,255,255,0.2);
}

.activity-item {
  display: flex;
  align-items: center;
  padding: 15px 0;
  border-bottom: 1px solid #e2e8f0;
}

.activity-item:last-child {
  border-bottom: none;
}

.activity-icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 15px;
  font-size: 16px;
  color: white;
}

.activity-content {
  flex: 1;
}

.activity-title {
  font-weight: 600;
  color: #2d3748;
  margin-bottom: 2px;
}

.activity-desc {
  font-size: 0.85rem;
  color: #718096;
}

.activity-time {
  font-size: 0.8rem;
  color: #a0aec0;
}

.status-badge {
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 500;
  margin-bottom: 5px;
  display: inline-block;
}

.status-normal {
  background: #c6f6d5;
  color: #22543d;
}

.status-warning {
  background: #feebc8;
  color: #744210;
}

.status-danger {
  background: #fed7d7;
  color: #742a2a;
}

.full-width {
  grid-column: 1 / -1;
}

.refresh-btn {
  position: fixed;
  bottom: 30px;
  right: 30px;
  width: 60px;
  height: 60px;
  border-radius: 50% !important;
  box-shadow: 0 4px 20px rgba(102, 126, 234, 0.3);
  transition: all 0.3s ease;
  z-index: 1000;
}

.refresh-btn:hover {
  transform: scale(1.1);
  box-shadow: 0 6px 25px rgba(102, 126, 234, 0.4);
}

.empty-state {
  text-align: center;
  padding: 40px;
  color: #718096;
}

@media (max-width: 768px) {
  .charts-section {
    grid-template-columns: 1fr;
  }

  .stats-grid {
    grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  }

  .header h1 {
    font-size: 2rem;
  }

  .dashboard {
    padding: 15px;
  }
}
</style>